import React, { useMemo } from 'react';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import type { LaneProps } from './Timeline';
import { beatTicks, secToPercent, spanBox } from './timelineGeometry';
import styles from './SongLane.module.scss';

/**
 * The song lane (design Timeline: "Song", locked). The editor answer has no waveform, so the lane
 * draws what the song map holds: one band per section with the height of its energy, and the beat
 * ticks (the bar lines on a long view). Nothing here is made up.
 */
const SongLaneComponent: React.FC<LaneProps> = ({ view }) => {
  const { songMap } = useMusicVideoEditor();
  const ticks = useMemo(
    () => beatTicks(songMap.beats, songMap.downbeats, view),
    [songMap.beats, songMap.downbeats, view]
  );
  const downbeats = useMemo(() => new Set(songMap.downbeats), [songMap.downbeats]);
  return (
    <div className={clsx(styles.lane, styles.hSong)} aria-hidden>
      {songMap.sections.map((section) => {
        const box = spanBox(section.startSec, section.endSec, view);
        if (box === null) {
          return null;
        }
        const height = 10 + Math.min(1, Math.max(0, section.energy)) * 70;
        return (
          <i
            key={section.index}
            className={styles.energy}
            style={{ left: `${box.left}%`, width: `calc(${box.width}% - 2px)`, height: `${height}%` }}
          />
        );
      })}
      {ticks.map((sec) => (
        <i
          key={sec}
          className={clsx(styles.beat, downbeats.has(sec) && styles.downbeat)}
          style={{ left: `${secToPercent(sec, view)}%` }}
        />
      ))}
    </div>
  );
};

export const SongLane = TypedMemo(SongLaneComponent);
